

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, Tag, Spin, message, Grid } from 'antd';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import http from '../api/http';

const COLORS = ['#2563eb', '#10b981', '#f59e42', '#f43f5e', '#a78bfa', '#fbbf24', '#60a5fa', '#34d399', '#f87171', '#6366f1'];

const StatCard = ({ title, value, icon, color, compact }) => (
  <Card size="small" bodyStyle={{ padding: compact ? 8 : 12 }} style={{ minHeight: compact ? 72 : 90 }}>
    <Statistic
      title={<span style={{ fontSize: compact ? 11 : 12 }}>{title}</span>}
      value={value}
      prefix={icon ? <span style={{ fontSize: compact ? 14 : 16, marginRight: 6 }}>{icon}</span> : null}
      valueStyle={{ color: color || '#2563eb', fontSize: compact ? 18 : 22 }}
    />
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  useEffect(() => {
    let mounted = true;
    http.get('/stats')
      .then(({ data }) => {
        if (!mounted) return;
        setStats(data);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        message.error('无法获取统计数据');
        setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  if (loading) return <Spin style={{marginTop:80}} size="large" />;
  if (!stats) return null;

  // 1. 人员与组织
  const deptData = (stats.deptDist||[]).map((d,i)=>({ name: d.department||'未分配', value: d.count, fill: COLORS[i%COLORS.length] }));
  // 2. 人事变动类型
  const changeTypeData = (stats.changeTypeDist||[]).map((d,i)=>({ name: d.change_type, value: d.count, fill: COLORS[i%COLORS.length] }));
  // 3. 培训类型
  const trainTypeData = (stats.trainTypeDist||[]).map((d,i)=>({ name: d.training_type||'未分类', value: d.count, fill: COLORS[i%COLORS.length] }));
  // 4. 门禁卡状态
  const cardStatusData = (stats.accessCardStatusDist||[]).map((d,i)=>({ name: d.status, value: d.count, fill: COLORS[i%COLORS.length] }));
  // 5. 角色分布
  const roleData = (stats.roleDist||[]).map((d,i)=>({ name: d.name, value: d.count, fill: COLORS[i%COLORS.length] }));

  const statGroups = [
    {
      title: '人员概览',
      items: [
        { title: '系统用户数', value: stats.userCount, icon: '👥', color: '#2563eb' },
        { title: '在职员工', value: stats.employeeCount, icon: '🧑‍💼', color: '#10b981' },
        { title: '临时工', value: stats.tempCount, icon: '🧑‍🔧', color: '#f59e42' },
        { title: '离职员工', value: stats.resignedCount, icon: '🚪', color: '#f43f5e' },
        { title: '本月离职', value: stats.resignedCountCurrentMonth ?? 0, icon: '📉', color: '#ef4444' },
        { title: '实习生', value: stats.internCount, icon: '🎓', color: '#a78bfa' }
      ]
    },
    {
      title: '人事变动',
      items: [
        { title: '人事变动', value: stats.hrChangeCount, icon: '🔄', color: '#fbbf24' },
        { title: '待审批变动', value: stats.pendingApproval, icon: '⏳', color: '#f87171' }
      ]
    },
    {
      title: '教育培训',
      items: [
        { title: '培训记录', value: stats.trainingCount, icon: '📚', color: '#60a5fa' },
        { title: '培训合格率', value: `${Math.round((stats.passRate || 0) * 100)}%`, icon: '✅', color: '#10b981' }
      ]
    },
    {
      title: '档案与专项',
      items: [
        { title: '档案数', value: stats.archiveCount, icon: '📁', color: '#6366f1' },
        { title: '专项监测', value: stats.specialMonitorCount, icon: '🩺', color: '#f59e42' },
        { title: '监测异常', value: stats.specialMonitorAbnormal, icon: '⚠️', color: '#f43f5e' }
      ]
    },
    {
      title: '保险情况',
      items: [
        { title: '参保人数', value: stats.insuredCount, icon: '🛡️', color: '#6366f1' },
        { title: '保险批次', value: stats.insuranceBatchCount, icon: '📦', color: '#a78bfa' }
      ]
    },
    {
      title: '日常运营',
      items: [
        { title: '门禁卡', value: stats.accessCardCount, icon: '🪪', color: '#2563eb' },
        { title: '临时用工记录', value: stats.tempWorkerCount, icon: '🧾', color: '#fbbf24' },
        { title: '工作笔记', value: stats.workNoteCount, icon: '🗒️', color: '#10b981' }
      ]
    }
  ];

  const chartHeight = isMobile ? 220 : 260;

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <h2 style={{ color: '#1f2937', marginBottom: isMobile ? 12 : 24, fontSize: isMobile ? 18 : 22 }}>数据看板</h2>
      <Row gutter={[12,12]}>
        {statGroups.map(group => (
          <Col key={group.title} xs={24} sm={12} md={8}>
            <Card size="small" title={group.title} bodyStyle={{ padding: isMobile ? 8 : 12 }}>
              <Row gutter={[8,8]}>
                {group.items.map(item => (
                    <Col key={`${group.title}-${item.title}`} xs={12} sm={12} md={8} lg={8} xl={8}>
                    <StatCard {...item} compact={isMobile} />
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 2. 主要分布图表 */}
      <Row gutter={[12,12]} style={{marginTop: isMobile ? 16 : 32}}>
        <Col xs={24} md={12} lg={8}>
          <Card title="部门人数分布" size="small">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <Pie data={deptData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={isMobile ? 65 : 80} label={!isMobile}>
                  {deptData.map((entry, idx) => <Cell key={entry.name} fill={entry.fill} />)}
                </Pie>
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card title="人事变动类型分布" size="small">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <Pie data={changeTypeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={isMobile ? 65 : 80} label={!isMobile}>
                  {changeTypeData.map((entry, idx) => <Cell key={entry.name} fill={entry.fill} />)}
                </Pie>
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card title="培训类型分布" size="small">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <Pie data={trainTypeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={isMobile ? 65 : 80} label={!isMobile}>
                  {trainTypeData.map((entry, idx) => <Cell key={entry.name} fill={entry.fill} />)}
                </Pie>
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card title="门禁卡状态分布" size="small">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <Pie data={cardStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={isMobile ? 65 : 80} label={!isMobile}>
                  {cardStatusData.map((entry, idx) => <Cell key={entry.name} fill={entry.fill} />)}
                </Pie>
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card title="角色分布" size="small">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={roleData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: isMobile ? 10 : 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                <Bar dataKey="value" fill="#2563eb">
                  {roleData.map((entry, idx) => <Cell key={entry.name} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;

